// src/components/Scratchpad.tsx
import React from "react";
import { useAuth, useScratchpad } from "../hooks";

function Scratchpad() {
  const { user } = useAuth();
  const { text, loading, error, saveError, saveState, updateText, clearText, flush } =
    useScratchpad(user?.uid);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateText(event.target.value);
  };

  // Notes are encrypted per account, so there's nowhere to keep a guest's.
  if (!user) {
    return (
      <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] px-3 py-2.5 text-sm text-[var(--color-text-tertiary)]">
        Sign in to keep notes here. They're encrypted to your account.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] p-4 flex flex-col gap-2 animate-pulse">
        <div className="h-32 bg-[var(--color-border)] rounded mb-2" />
        <div className="h-6 w-20 bg-[var(--color-border)] rounded self-end" />
      </div>
    );
  }

  if (error && error.message.includes("decrypt")) {
    return (
      <div className="rounded-[var(--radius-md)] bg-[color-mix(in_srgb,var(--color-danger)_5%,transparent)] border border-[color-mix(in_srgb,var(--color-danger)_20%,transparent)] p-4 flex flex-col gap-3">
        <div className="font-semibold text-[var(--color-danger)] flex items-center gap-2">
          <svg
            className="w-5 h-5 text-[var(--color-danger)]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
            />
          </svg>
          Failed to decrypt your Scratchpad
        </div>
        <div className="text-sm text-[var(--color-danger)]">
          This may be due to a login system change or data corruption. You can reset your Scratchpad
          to start fresh.{" "}
          <span className="block mt-1">(Your old encrypted note will be lost.)</span>
        </div>
        <button
          className="px-3 py-1 rounded bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)] text-xs font-medium hover:bg-[var(--color-border)] border border-[var(--color-border)] self-end"
          onClick={() => clearText()}
        >
          Reset Scratchpad
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] p-4 flex flex-col gap-2">
        <div className="text-[var(--color-danger)]">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-0 flex flex-col gap-0 transition-colors relative">
      <textarea
        className="w-full min-h-[180px] max-h-96 p-4 rounded-[var(--radius-md)] bg-[var(--color-bg)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border-0 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)] transition-colors resize-vertical text-sm"
        value={text}
        onChange={handleTextChange}
        onBlur={() => void flush()}
        placeholder="Type here to save a quick note to self."
      />
      {/* Save status sits alongside the note. A failed save used to replace the
          whole editor with an error box, hiding the text the user just typed. */}
      {saveError ? (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-[var(--color-warning)] border-t border-[var(--color-border)]">
          <span className="truncate" title={saveError.message}>
            Not saved to the cloud — kept on this device.
          </span>
          <button
            type="button"
            onClick={() => void flush()}
            className="shrink-0 underline hover:opacity-80"
          >
            Retry
          </button>
        </div>
      ) : saveState === "saving" ? (
        <div className="px-3 py-1.5 text-xs text-[var(--color-text-muted)] border-t border-[var(--color-border)]">
          Saving…
        </div>
      ) : null}
    </div>
  );
}

export default Scratchpad;
